import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { SubscriptionOptionEnum } from "@shared_packages/toolkit/types";
import {
  Button,
  ButtonVariant,
  Checkbox,
  Search,
  SwitchButton,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import SubscriptionWizardModal from "../components/SubscriptionWizardModal";
import {
  euPaymentCurrencies,
  resolveEuPaymentsState,
  subscriptionsTabPath,
} from "./company-eu-payments.helpers";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";
import api from "../../../../util/api";
import {
  ForbiddenPaymentScope,
  FullCompanyApiType,
} from "../../../../util/types";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding-top: 20px;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DescText = styled(Typography)`
  color: #6b7a8d;
  line-height: 1.5;
`;

const HintText = styled(Typography)`
  color: #8a9bb0;
`;

const ForbiddenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px 14px;
`;

const CountryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
`;

const GroupLabel = styled(Typography)`
  font-weight: 600;
  color: #6b7a8d;
  padding-top: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const countryLabel = (code: string) => {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

const toggle = (set: Set<string>, value: string) => {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
};

/**
 * Admin-set block list. Unlike the merchant's own checkout customization, a checked box here
 * means "forbidden", and the merchant cannot lift it from their own settings.
 */
const ForbiddenPaymentScopeSection: React.FC<{
  companyId: string;
  canEdit: boolean;
}> = ({ companyId, canEdit }) => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState<Set<string>>(new Set());
  const [countries, setCountries] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["company-forbidden-payment-scope", companyId],
    queryFn: () => api.getCompanyForbiddenPaymentScope(companyId),
  });

  useEffect(() => {
    if (!data) return;
    setProviders(new Set(data.forbiddenProviders));
    setCountries(new Set(data.forbiddenCountries));
  }, [data]);

  // Grouped by country so a picker over the whole EU catalogue stays navigable.
  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const groups = new Map<string, ForbiddenPaymentScope["providers"]>();
    (data?.providers ?? [])
      .filter(
        (provider) =>
          !needle ||
          provider.name.toLowerCase().includes(needle) ||
          provider.externalId.toLowerCase().includes(needle)
      )
      .forEach((provider) => {
        const list = groups.get(provider.countryId) ?? [];
        list.push(provider);
        groups.set(provider.countryId, list);
      });
    return Array.from(groups.entries());
  }, [data, search]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.setCompanyForbiddenPaymentScope(
        companyId,
        Array.from(providers),
        Array.from(countries)
      );
      showNotification("Successfully updated", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section>
      <SectionTitle variant={TypographyVariant.HEADER3}>
        Forbidden providers and countries
      </SectionTitle>
      <DescText variant={TypographyVariant.BODY4}>
        Banks ticked here are hidden from this company&apos;s payment pages and
        refused at payment initiation, whatever the merchant sets in their own
        checkout customization. Ticking a country blocks every bank in it.
        Applies to UK banks as well as EU ones.
      </DescText>
      {!canEdit && (
        <HintText variant={TypographyVariant.BODY4}>
          You need the &quot;actions on companies&quot; permission to change
          this.
        </HintText>
      )}

      {isLoading ? (
        <HintText variant={TypographyVariant.BODY4}>Loading…</HintText>
      ) : (
        <>
          <Typography variant={TypographyVariant.BODY3}>
            Countries ({countries.size} forbidden)
          </Typography>
          <ForbiddenList>
            <CountryGrid>
              {(data?.countries ?? []).map((code) => (
                <Checkbox
                  key={code}
                  checked={countries.has(code)}
                  disabled={!canEdit}
                  onChange={() => setCountries(toggle(countries, code))}
                  label={`${countryLabel(code)} (${code})`}
                />
              ))}
            </CountryGrid>
          </ForbiddenList>

          <Row>
            <Typography variant={TypographyVariant.BODY3}>
              Providers ({providers.size} forbidden)
            </Typography>
            <Search
              placeholder="Search banks"
              value={search}
              onChangeHandler={setSearch}
              onCrossHandler={() => setSearch("")}
            />
          </Row>
          <ForbiddenList>
            {grouped.map(([code, list]) => (
              <React.Fragment key={code}>
                <GroupLabel variant={TypographyVariant.BODY4}>
                  {countryLabel(code)} ({code})
                </GroupLabel>
                {list.map((provider) => (
                  <Checkbox
                    key={provider._id}
                    checked={providers.has(provider._id)}
                    disabled={!canEdit}
                    onChange={() =>
                      setProviders(toggle(providers, provider._id))
                    }
                    label={provider.name}
                  />
                ))}
              </React.Fragment>
            ))}
            {!grouped.length && (
              <HintText variant={TypographyVariant.BODY4}>
                No banks match that search.
              </HintText>
            )}
          </ForbiddenList>

          <Row>
            <span />
            <Button
              onClick={() => void handleSave()}
              loading={saving}
              disabled={saving || !canEdit}
            >
              Save
            </Button>
          </Row>
        </>
      )}
    </Section>
  );
};

interface Props {
  companyId: string;
  /** Regular company: the tab follows the EU subscription. Managed: the flag switch stays. */
  variant: "company" | "managed";
  /** Current flag value — only the managed switch reads it. */
  enabled: boolean;
  company?: FullCompanyApiType;
  onRefresh?: () => void | Promise<void>;
}

const ManagedEuSwitch: React.FC<{
  companyId: string;
  enabled: boolean;
  canEdit: boolean;
  onRefresh?: () => void | Promise<void>;
}> = ({ companyId, enabled, canEdit, onRefresh }) => {
  const { showNotification } = useNotification();
  const [euEnabled, setEuEnabled] = useState(enabled);
  const [toggling, setToggling] = useState(false);

  useEffect(() => setEuEnabled(enabled), [enabled]);

  const handleToggle = async (value: boolean) => {
    setToggling(true);
    try {
      await api.setCompanyInternationalObAccess(companyId, value);
      setEuEnabled(value);
      onRefresh();
      showNotification("Successfully updated", NotificationTypes.SUCCESS);
      void onRefresh?.();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    } finally {
      setToggling(false);
    }
  };

  return (
    <Section>
      <SectionTitle variant={TypographyVariant.HEADER3}>
        EU payments
      </SectionTitle>
      <DescText variant={TypographyVariant.BODY4}>
        A partner-managed company holds no subscription, so this switch is its
        whole EU/international Open Banking gate (updates{" "}
        <code>canAccessInternationalOb</code>). Enabling grants every EU payment
        currency (EUR, SEK, CZK, RON, PLN, HUF, DKK) on top of GBP and lets the
        company connect non-UK bank accounts. Disabling only turns the gate off;
        GBP and the currency list stay as they are.
      </DescText>
      {!canEdit && (
        <HintText variant={TypographyVariant.BODY4}>
          You need the &quot;actions on companies&quot; permission to change
          this.
        </HintText>
      )}
      <SwitchRow>
        <SwitchButton
          checked={euEnabled}
          onChange={(val: boolean) => void handleToggle(val)}
          disabled={toggling || !canEdit}
        />
        <Typography variant={TypographyVariant.BODY3}>
          {euEnabled
            ? "EU payments enabled, all EU currencies"
            : "EU payments disabled"}
        </Typography>
      </SwitchRow>
    </Section>
  );
};

const CompanyEuSubscriptionSection: React.FC<{
  companyId: string;
  company?: FullCompanyApiType;
  canEdit: boolean;
  onRefresh?: () => void | Promise<void>;
}> = ({ companyId, company, canEdit, onRefresh }) => {
  const [showWizard, setShowWizard] = useState(false);
  const state = resolveEuPaymentsState(company);
  const currencies = euPaymentCurrencies(company);
  const subscriptionsLink = (
    <Link to={subscriptionsTabPath(companyId)}>Subscriptions tab</Link>
  );

  return (
    <Section>
      <SectionTitle variant={TypographyVariant.HEADER3}>
        EU payments
      </SectionTitle>
      <DescText variant={TypographyVariant.BODY4}>
        EU/international Open Banking for a company comes from its EU Payments
        Only subscription: activating it sets the company flag and the enabled
        payment currencies, and the payment API checks the subscription, not the
        flag alone. There is no separate switch.
      </DescText>

      {!company && (
        <HintText variant={TypographyVariant.BODY4}>Loading…</HintText>
      )}

      {company && state.kind === "none" && (
        <>
          <Typography variant={TypographyVariant.BODY3}>
            No EU Payments Only subscription yet. Configure the EU payments
            subscription first.
          </Typography>
          {company.canAccessInternationalOb === true && (
            <HintText variant={TypographyVariant.BODY4}>
              The legacy flag <code>canAccessInternationalOb</code> is on, but
              without a subscription EU payments are only let through in shadow
              mode and reported to Sentry.
            </HintText>
          )}
          {!canEdit && (
            <HintText variant={TypographyVariant.BODY4}>
              You need the &quot;actions on companies&quot; permission to
              configure it.
            </HintText>
          )}
          <Row>
            <span />
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={() => setShowWizard(true)}
              disabled={!canEdit}
            >
              Pre-configure EU subscription
            </Button>
          </Row>
        </>
      )}

      {company && state.kind === "preconfigured" && (
        <Typography variant={TypographyVariant.BODY3}>
          The EU Payments Only subscription is preconfigured but not active yet.
          Activate it, or let the customer activate it, on the{" "}
          {subscriptionsLink}.
        </Typography>
      )}

      {company && state.kind === "live" && (
        <>
          <Typography variant={TypographyVariant.BODY3}>
            EU payments are enabled by the EU Payments Only subscription (
            {state.status}).
          </Typography>
          <Typography variant={TypographyVariant.BODY3}>
            Payment currencies:{" "}
            {currencies.length ? currencies.join(", ") : "none configured"}.
            Change currencies or fees on the {subscriptionsLink}.
          </Typography>
        </>
      )}

      {showWizard && company && (
        <SubscriptionWizardModal
          data={company}
          lockedType={SubscriptionOptionEnum.PAYMENTS_ONLY_EU}
          onClose={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            void onRefresh?.();
          }}
        />
      )}
    </Section>
  );
};

export const CompanyEuPaymentsTab: React.FC<Props> = ({
  companyId,
  variant,
  enabled,
  company,
  onRefresh,
}) => {
  const { authUser } = useAuthUserContext();
  const canEdit = authUser.policies?.actionsOnCompanies === true;

  return (
    <Wrap>
      {variant === "managed" ? (
        <ManagedEuSwitch
          companyId={companyId}
          enabled={enabled}
          canEdit={canEdit}
          onRefresh={onRefresh}
        />
      ) : (
        <CompanyEuSubscriptionSection
          companyId={companyId}
          company={company}
          canEdit={canEdit}
          onRefresh={onRefresh}
        />
      )}

      {/* Blocks UK banks as well as EU ones, so it stays whatever the EU state. */}
      <ForbiddenPaymentScopeSection companyId={companyId} canEdit={canEdit} />
    </Wrap>
  );
};

export default CompanyEuPaymentsTab;
